import { Popover, Text, Button, Group } from '@mantine/core';
import { useState } from 'react';

interface DeleteConfirmationPopoverProps {
  children: React.ReactNode;
  onConfirm: () => Promise<void> | void;
  isLoading?: boolean;
  title?: string;
  description?: string;
  opened?: boolean;
  onClose?: () => void;
}

export function DeleteConfirmationPopover({
  children,
  onConfirm,
  isLoading = false,
  title = 'Delete this item?',
  description = 'You cannot undo this action.',
  opened: controlledOpened,
  onClose: controlledOnClose,
}: DeleteConfirmationPopoverProps) {
  const [internalOpened, setInternalOpened] = useState(false);

  const isControlled = controlledOpened !== undefined;
  const opened = isControlled ? controlledOpened : internalOpened;

  const handleConfirm = async () => {
    await onConfirm();
    if (isControlled) {
      controlledOnClose?.();
    } else {
      setInternalOpened(false);
    }
  };

  const handleCancel = () => {
    if (isControlled) {
      controlledOnClose?.();
    } else {
      setInternalOpened(false);
    }
  };

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isControlled) {
      if (opened) {
        controlledOnClose?.();
      }
    } else {
      setInternalOpened(!internalOpened);
    }
  };

  const handleChange = (newOpened: boolean) => {
    if (isControlled) {
      if (!newOpened) {
        controlledOnClose?.();
      }
    } else {
      setInternalOpened(newOpened);
    }
  };

  return (
    <Popover
      width={280}
      position="bottom-start"
      withArrow
      shadow="md"
      opened={opened}
      onChange={handleChange}
      closeOnClickOutside={true}
      closeOnEscape={true}
      zIndex={1100}
    >
      <Popover.Target>
        <div onClick={isControlled ? undefined : handleToggle} style={{ display: 'inline-block' }}>
          {children}
        </div>
      </Popover.Target>
      <Popover.Dropdown>
        <Text size="sm" fw={500} mb="xs">
          {title}
        </Text>
        <Text size="sm" c="dimmed" mb="md">
          {description}
        </Text>
        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" color="gray" onClick={handleCancel} disabled={isLoading} size="xs">
            Cancel
          </Button>
          <Button color="red" onClick={handleConfirm} loading={isLoading} disabled={isLoading} size="xs">
            Delete
          </Button>
        </Group>
      </Popover.Dropdown>
    </Popover>
  );
}
